// ./src/components/features/Forms/FormFieldsEditModal/PhoneFieldOptionsEditor.tsx
import React from "react";
import {
  Stack,
  Switch,
  TextInput,
  NumberInput,
  Text,
  Group,
} from "@mantine/core";
import { PhoneFieldOptions } from "./fieldOptionsTypes";

export interface PhoneFieldOptionsEditorProps {
  value: PhoneFieldOptions;
  onChange: (options: PhoneFieldOptions) => void;
  minValue?: number | null;
  maxValue?: number | null;
  onMinValueChange?: (value: number | null) => void;
  onMaxValueChange?: (value: number | null) => void;
  maxValueError?: string;
}

export const PhoneFieldOptionsEditor: React.FC<
  PhoneFieldOptionsEditorProps
> = ({
  value,
  onChange,
  minValue,
  maxValue,
  onMinValueChange,
  onMaxValueChange,
  maxValueError,
}) => {
  const isInternational = value.requireInternational ?? false;

  const handleChange = (field: keyof PhoneFieldOptions, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const handleAreaCodesChange = (text: string) => {
    const codes = text
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c);
    onChange({
      ...value,
      allowedAreaCodes: codes.length > 0 ? codes : undefined,
    });
  };

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Configure phone number validation options.
      </Text>

      {onMinValueChange && onMaxValueChange && (
        <Group grow>
          <NumberInput
            label="Min Length"
            placeholder="No min"
            value={minValue ?? ""}
            onChange={(val) =>
              onMinValueChange(val === "" ? null : Number(val))
            }
            min={0}
            size="sm"
          />
          <NumberInput
            label="Max Length"
            placeholder="No max"
            value={maxValue ?? ""}
            onChange={(val) =>
              onMaxValueChange(val === "" ? null : Number(val))
            }
            error={maxValueError}
            min={minValue ?? 0}
            size="sm"
          />
        </Group>
      )}

      <Switch
        label="Require international format"
        description="Phone must include country code (e.g., +1 234 567 8900)"
        checked={isInternational}
        onChange={(e) =>
          handleChange("requireInternational", e.currentTarget.checked)
        }
        size="sm"
      />

      <NumberInput
        label={
          isInternational ? "Country + Area Code Length" : "Area Code Length"
        }
        placeholder={isInternational ? "Default (4 for +1xxx)" : "Default (3)"}
        description={
          isInternational
            ? "Total length of country code + area code combined (e.g., 4 for +1403)"
            : "Expected length of area code only (e.g., 3 for 403)"
        }
        value={value.areaCodeLength ?? ""}
        onChange={(val) =>
          handleChange("areaCodeLength", val === "" ? undefined : val)
        }
        min={1}
        max={15}
        size="sm"
      />

      <TextInput
        label={
          isInternational
            ? "Allowed Country + Area Codes"
            : "Allowed Area Codes"
        }
        placeholder={isInternational ? "+1403, +1587, +44207" : "403, 587, 780"}
        description={
          isInternational
            ? "Comma-separated list of allowed country + area code combinations (e.g., +1403 for Canada/Calgary). Leave empty for any."
            : "Comma-separated list of allowed area codes (e.g., 403). Leave empty for any."
        }
        value={value.allowedAreaCodes?.join(", ") ?? ""}
        onChange={(e) => handleAreaCodesChange(e.target.value)}
        size="sm"
      />
    </Stack>
  );
};
